
import React, {useState, useEffect} from 'react';
import {array, number, string, bool, func, shape, element, object, arrayOf} from 'prop-types';
import {cloneDeep, isEmpty} from 'lodash';
import {BRUSH_STROKES} from '../chart-colors.js';
import {
    Bar, Brush,
    CartesianGrid,
    Cell,
    ComposedChart,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import {DateUtil} from '../../../utils/date-util.js';
import {CHART_X_AXIS_PADDING_LEFT, CHART_X_AXIS_PADDING_RIGHT} from '../chart-util.js';
import {
    getBarChartConfig, getBarCellBackgroundFill, getBarCellFill
} from './bar-chart-wrapper-util.js';


const BarChartWrapper = ({chartData, configChart}) => {
    const [data, setData] = useState(chartData);

    const {
        chartWidth,
        chartHeight,
        plotBars,
        isHighLightBarOnClick,
        isHighLightCellOnClick,
        isStackedBars,
        barSize,
        barStrokes,
        isBarRoundedEdge,
        barBackgroundColor,
        barOnClickHandler,
        xAxisKey,
        xAxisTickFormatter,
        yAxisTickFormatter,
        groupKey,
        groupTickFormatter,
        groupTick,
        legendLabels,
        legendIcon,
        legendTextColor,
        isToggleChartOnClickOfLegend,
        isVisibleHorizontalGrid,
        tooltipContent,
        brush,
        getBarEdge
    } = getBarChartConfig(configChart, data || []);

    const [displayedBarKeys, setDisplayedBarKeys] = useState(plotBars);
    const [selectedBarKey, setSelectedBarKey] = useState({});

    let barIterateIndex = 0;

    const barEdgeStyle = getBarEdge(displayedBarKeys, isBarRoundedEdge);
    const barCellFillStyle = getBarCellFill(selectedBarKey, isHighLightBarOnClick, isHighLightCellOnClick);
    const barBackgroundFillStyle = getBarCellBackgroundFill(isStackedBars, barBackgroundColor, barEdgeStyle);

    const handleOnClickBarOrCell = clickedItemData => {
        isEmpty(selectedBarKey) || (!isHighLightCellOnClick && !isHighLightBarOnClick) ||
        (isHighLightCellOnClick && selectedBarKey.cellId !== clickedItemData.cellId) ||
        (!isHighLightCellOnClick && isHighLightBarOnClick && selectedBarKey.cellIndex !== clickedItemData.cellIndex) ?
            setSelectedBarKey(clickedItemData) : setSelectedBarKey({});
    };

    const handleOnClickLegendItem = legendItem => {
        const foundAt = displayedBarKeys.indexOf(legendItem.dataKey);
        const copyOfDataKeys = cloneDeep(displayedBarKeys);
        foundAt < 0 ? copyOfDataKeys.push(legendItem.dataKey) : copyOfDataKeys.splice(foundAt, 1);
        setDisplayedBarKeys(copyOfDataKeys);
        setSelectedBarKey({});
    };

    const renderColorfulLegendText = (legendLabel, entry) => {
        const labelText = legendLabels ? legendLabels[entry.payload.dataKey] : legendLabel;
        const legendFontColor = legendTextColor === '' ? entry.color : legendTextColor;
        return <span style={{ color: legendFontColor }}>{labelText}</span>;
    };

    useEffect(() => barOnClickHandler && barOnClickHandler(selectedBarKey), [barOnClickHandler, selectedBarKey]);

    useEffect(() => {
        setData(chartData);
    }, [chartData]);

    useEffect(() => {
        setDisplayedBarKeys(plotBars);
    }, [plotBars]);

    return (isEmpty(data) || isEmpty(plotBars)) ? <div style={{height: '20rem'}}><h4 className='no-records-found'>No Records Found</h4></div> :
        <ResponsiveContainer
            width={chartWidth}
            height={chartHeight ? chartHeight : 400}
            debounce={500}
            key={DateUtil.getNowMoment()} //TODO: to implement in a better way
        >
            <ComposedChart
                data={data}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
                {isVisibleHorizontalGrid && <CartesianGrid vertical={false} />}

                {!isEmpty(data) && <XAxis
                    xAxisId={0}
                    padding={{
                        left: CHART_X_AXIS_PADDING_LEFT,
                        right: CHART_X_AXIS_PADDING_RIGHT
                    }}
                    dataKey={xAxisKey}
                    tickFormatter={xAxisTickFormatter}
                />}

                {!isEmpty(data) && !isEmpty(groupKey) && <XAxis
                    xAxisId={1}
                    padding={{
                        left: CHART_X_AXIS_PADDING_LEFT,
                        right: CHART_X_AXIS_PADDING_RIGHT
                    }}
                    dataKey={groupKey}
                    interval={0}
                    axisLine={false}
                    tickLine={false}
                    tick={groupTick(data)}
                    tickFormatter={groupTickFormatter}
                    height={35}
                />}

                {!isEmpty(data) && <YAxis tickFormatter={yAxisTickFormatter} />}

                {!isEmpty(data) &&
                plotBars.map((barKey, barIndex) => {
                    const barRadius = barEdgeStyle(barIterateIndex);
                    const background = barBackgroundFillStyle(barIterateIndex);
                    barIterateIndex = displayedBarKeys.indexOf(barKey) < 0 ? barIterateIndex : (barIterateIndex + 1);

                    return (
                        <Bar
                            background={background}
                            radius={barRadius}
                            hide={displayedBarKeys.indexOf(barKey) < 0}
                            key={`bars_${barIndex}`}
                            dataKey={barKey}
                            stackId={`stacked-bar-chart${isStackedBars === true ? '' : barIndex}`}
                            barSize={barSize}
                            onClick={
                                barOnClickHandler &&
                                (pickedData => handleOnClickBarOrCell(pickedData))
                            }
                            fill={barStrokes[barIndex]}
                        >
                            {!isEmpty(data) && data.map((entry, cellIndex) => {
                                const cIndex = `bar-${cellIndex}`;
                                const cId = `cell-${barKey}-${cellIndex}`;
                                return (
                                    <Cell
                                        key={cId}
                                        cellIndex={cIndex}
                                        cellId={cId}
                                        dataKey={barKey}
                                        cursor='pointer'
                                        fill={barCellFillStyle(cIndex, cId, barStrokes[barIndex])}
                                        strokeWidth={4}
                                    />
                                );
                            })}
                        </Bar>
                    );
                })}

                {tooltipContent ? <Tooltip content={tooltipContent} /> : <Tooltip />}

                {!isEmpty(data) && !isEmpty(plotBars) && <Legend
                    wrapperStyle={{
                        paddingTop: '10px'
                    }}
                    iconType={legendIcon}
                    formatter={renderColorfulLegendText}
                    onClick={isToggleChartOnClickOfLegend ? handleOnClickLegendItem : () => {}}
                />}

                {!isEmpty(data) && !isEmpty(brush) && (
                    <Brush
                        height={20}
                        dataKey={xAxisKey}
                        startIndex={brush.startIndex}
                        endIndex={brush.endIndex}
                        stroke={BRUSH_STROKES[0]}
                        tickFormatter={brush.tickFormatter}
                    />
                )}
            </ComposedChart>
        </ResponsiveContainer>;
};

BarChartWrapper.propTypes = {
    chartData: array.isRequired,
    configChart: shape({
        chartTitle: string.isRequired,
        chartWidth: number,
        chartHeight: number,
        configBars: shape({
            plotBars: arrayOf(string).isRequired,
            isHighLightBarOnClick: bool,
            isHighLightCellOnClick: bool,
            isStackedBars: bool,
            barSize: number,
            barStrokes: object,
            isBarRoundedEdge: bool,
            isSetBarBackground: bool,
            barBackgroundColor: string,
            barOnClickHandler: func
        }).isRequired,
        configXAxis: shape({
            xAxisKey: string.isRequired,
            xAxisTickFormatter: func
        }).isRequired,
        configYAxis: shape({
            yAxisTickFormatter: func
        }),
        chartFilters: element,
        configToggle: shape({
            toggleName: string.isRequired,
            className: string,
            toggle: arrayOf(
                shape({
                    toggleLabel: string.isRequired,
                    toggleValue: string.isRequired,
                    className: string
                })).isRequired
        }.isRequired),
        configLegends: shape({
            legendLabels: object,
            isToggleChartOnClickOfLegend: bool,
            legendIcon: string,
            isLegendTextColorSameAsChartColor: bool
        }),
        isVisibleHorizontalGrid: bool,
        tooltipContent: element
    }),
    apiErrorMessage: object,
    toolbarConfig: shape({
        isShowToolbar: bool,
        refreshIconOnClickHandler: func,
        barChartIconOnClickHandler: func,
        gridChartIconOnClickHandler: func
    })
};

export default BarChartWrapper;
